import type { BusinessPolicySnapshot } from '../policy/business-policy.ts';
import type { OrderLineSnapshot } from '../order/product-selection.ts';
import type { PricingAdjustment } from '../order/order-finalization.ts';
import { quoteForCommit, type QuoteResult, type QuoteSuccess } from './quote.ts';
import { resolveConfirmedComboMatches, type ConfirmedComboMatchInput } from './explicit-combo.ts';
import type { PricingUnit } from './combo.ts';

export function applyConfirmedComboMatchesToQuote(
  base: QuoteResult,
  lines: readonly OrderLineSnapshot[],
  policy: Pick<BusinessPolicySnapshot, 'comboRules'>,
  confirmedComboMatches: readonly ConfirmedComboMatchInput[],
): QuoteResult {
  if (base.kind !== 'quoted') return base;
  const pricingByLine = new Map(base.evidence.pricing.lines.map((line) => [line.lineId, line]));
  const units: PricingUnit[] = [];
  for (const line of lines) {
    const priced = pricingByLine.get(line.lineId);
    if (!priced) throw new Error(`PRICING_EXPLICIT_COMBO_LINE_NOT_PRICED:${line.lineId}`);
    for (let unitIndex = 0; unitIndex < line.quantity; unitIndex += 1) {
      units.push({
        lineId: line.lineId,
        unitIndex,
        productId: line.productId,
        selectorKeys: [...line.selectorKeys],
        baseMinor: priced.unitBaseMinor,
      });
    }
  }
  const matches = resolveConfirmedComboMatches(units, policy.comboRules, confirmedComboMatches);
  const adjustments: PricingAdjustment[] = matches
    .map((match) => ({
      kind: 'combo' as const,
      referenceId: match.ruleId,
      amountMinor: match.fixedBaseTotalMinor - match.originalBaseTotalMinor,
      consumedUnits: match.consumedUnits.map((unit) => ({ ...unit })),
    }))
    .filter((adjustment) => adjustment.amountMinor !== 0);
  const totalMinor =
    base.evidence.pricing.lines.reduce((sum, line) => sum + line.lineSubtotalMinor, 0) +
    adjustments.reduce((sum, adjustment) => sum + adjustment.amountMinor, 0);
  if (!Number.isSafeInteger(totalMinor) || totalMinor < 0) throw new Error('PRICING_TOTAL_INVALID');
  const result: QuoteSuccess = {
    ...base,
    comboMatches: matches.map((match) => ({
      ruleId: match.ruleId,
      consumedUnits: match.consumedUnits.map((unit) => ({ ...unit })),
    })),
    evidence: {
      ...base.evidence,
      pricing: {
        ...base.evidence.pricing,
        totalMinor,
        adjustments,
      },
    },
  };
  return result;
}

export function quoteForExplicitComboCommit(
  lines: readonly OrderLineSnapshot[],
  policy: BusinessPolicySnapshot,
  confirmedComboMatches: readonly ConfirmedComboMatchInput[],
): QuoteResult {
  return applyConfirmedComboMatchesToQuote(
    quoteForCommit(lines, policy),
    lines,
    policy,
    confirmedComboMatches,
  );
}
